import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { EventData } from "@/contexts/event/EventContext";
import { useSupabase } from "@/contexts/supabase";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

interface CancelEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: EventData | null;
  scope: 'single' | 'future';
  onCancelled: () => void;
}

export function CancelEventDialog({ open, onOpenChange, event, scope, onCancelled }: CancelEventDialogProps) {
  const supabase = useSupabase();
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const participantCount = event?.participants?.length || 0;
  const isRecurring = !!event?.parentEventId || event?.isRecurringMaster;

  const handleCancel = async () => {
    if (!event) return;

    setLoading(true);
    try {
      const action = scope === 'single' ? 'cancel_event' : 'cancel_future_series';
      
      const { error } = await (supabase as any).functions.invoke('manage-event', {
        body: {
          action,
          eventId: event.id,
          reason: reason.trim() || undefined
        }
      });

      if (error) throw error;

      toast.success(`Event${scope === 'future' ? 's' : ''} cancelled successfully`);
      onCancelled();
      onOpenChange(false);
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error('Failed to cancel event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Cancel Event{scope === 'future' ? 's' : ''}
          </DialogTitle>
          <DialogDescription>
            {scope === 'single' && !isRecurring && (
              <>This will cancel the event. All {participantCount} participant{participantCount !== 1 ? 's' : ''} will be notified.</>
            )}
            {scope === 'single' && isRecurring && (
              <>This will cancel only this occurrence. Participants for this event will be notified.</>
            )}
            {scope === 'future' && (
              <>This will cancel this event and all future occurrences in the series. All participants will be notified.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Cancellation Reason (Optional)</Label>
            <Textarea
              placeholder="e.g., Weather conditions, venue unavailable..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Keep Event
          </Button>
          <Button variant="destructive" onClick={handleCancel} disabled={loading}>
            {loading ? 'Cancelling...' : `Cancel Event${scope === 'future' ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
